// useAgora — wraps the Agora Web SDK for the user (business-portal) side.
// Joins a channel using credentials minted by Laravel (appId/channel/token/uid),
// publishes local mic+camera, tracks remote users, and exposes call controls.
import { useCallback, useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";

AgoraRTC.setLogLevel(2);

/**
 * @param {object|null} creds { appId, channel, token, uid } or null to stay idle.
 */
export function useAgora(creds) {
  const clientRef = useRef(null);
  const localAudioRef = useRef(null);
  const localVideoRef = useRef(null);
  const joinedRef = useRef(false);

  const [status, setStatus] = useState("idle"); // idle|connecting|connected|failed|left
  const [error, setError] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const speakerOnRef = useRef(true);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [canFlip, setCanFlip] = useState(false);
  const camerasRef = useRef([]);
  const camIdxRef = useRef(0);
  // Output-device (speaker/headphones) selection — desktop Chrome/Edge only.
  const [playbackDevices, setPlaybackDevices] = useState([]);
  const [activeSpeaker, setActiveSpeaker] = useState("");
  const activeSpeakerRef = useRef("");

  useEffect(() => {
    if (!creds || !creds.appId || !creds.channel) return;
    let cancelled = false;

    const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    clientRef.current = client;

    const sync = () => setRemoteUsers(Array.from(client.remoteUsers));

    client.on("user-published", async (user, mediaType) => {
      console.log("[call][user] remote user-published", user.uid, mediaType);
      await client.subscribe(user, mediaType);
      if (mediaType === "audio") {
        user.audioTrack?.play();
        // Honour the current speaker state for late-joining/republished audio.
        user.audioTrack?.setVolume(speakerOnRef.current ? 100 : 0);
        // Route to the chosen output device (desktop only; no-op elsewhere).
        if (activeSpeakerRef.current) {
          try { await user.audioTrack?.setPlaybackDevice(activeSpeakerRef.current); } catch (e) {}
        }
      }
      sync();
    });
    client.on("user-unpublished", sync);
    client.on("user-joined", (user) => { console.log("[call][user] remote user-joined", user.uid); sync(); });
    client.on("user-left", sync);
    client.on("connection-state-change", (cur, prev, reason) =>
      console.log("[call][user] conn-state", prev, "->", cur, reason || "")
    );

    (async () => {
      try {
        setStatus("connecting");
        console.log("[call][user] joining", {
          channel: creds.channel, uid: creds.uid, appId: creds.appId, hasToken: !!creds.token,
        });
        await client.join(creds.appId, creds.channel, creds.token || null, creds.uid);
        console.log("[call][user] JOINED channel", creds.channel, "as uid", creds.uid);
        joinedRef.current = true;
        // If we were torn down during the async join, leave so we don't hold
        // the uid (which would cause UID_CONFLICT on the next join attempt).
        if (cancelled) {
          await client.leave().catch(() => {});
          return;
        }

        const [audioTrack, videoTrack] =
          await AgoraRTC.createMicrophoneAndCameraTracks({}, { encoderConfig: "480p_1" });
        if (cancelled) {
          audioTrack.close();
          videoTrack.close();
          await client.leave().catch(() => {});
          return;
        }
        localAudioRef.current = audioTrack;
        localVideoRef.current = videoTrack;
        setLocalVideoTrack(videoTrack);

        // Discover the available cameras so we can offer front/back flipping.
        try {
          const cams = await AgoraRTC.getCameras();
          camerasRef.current = cams || [];
          const curLabel = videoTrack.getTrackLabel?.();
          const idx = camerasRef.current.findIndex((c) => c.label === curLabel);
          camIdxRef.current = idx >= 0 ? idx : 0;
          setCanFlip(camerasRef.current.length > 1);
        } catch (e) {
          /* device enumeration blocked — hide the flip control */
        }

        // Enumerate audio output devices (desktop Chrome/Edge). On browsers that
        // don't support output selection this stays empty and the picker hides.
        try {
          if (typeof AgoraRTC.getPlaybackDevices === "function") {
            const outs = await AgoraRTC.getPlaybackDevices();
            if (!cancelled) setPlaybackDevices((outs || []).filter((d) => d.deviceId));
          }
        } catch (e) {
          /* output selection unsupported — picker stays hidden */
        }

        await client.publish([audioTrack, videoTrack]);
        console.log("[call][user] PUBLISHED local tracks");
        if (!cancelled) setStatus("connected");
      } catch (err) {
        if (cancelled) return;
        console.error("[call][user] join failed", err);
        setError(err.message || "Failed to start the call.");
        setStatus("failed");
      }
    })();

    return () => {
      cancelled = true;
      const c = clientRef.current;
      localAudioRef.current?.close();
      localVideoRef.current?.close();
      localAudioRef.current = null;
      localVideoRef.current = null;
      setLocalVideoTrack(null);
      client.removeAllListeners();
      // Always attempt to leave the client we created (safe even if the join
      // is still in flight) so we never leave a stale joined client behind.
      (c || client)?.leave().catch(() => {});
      joinedRef.current = false;
      setRemoteUsers([]);
      setStatus("idle");
      setMicOn(true);
      setCamOn(true);
      setSpeakerOn(true);
      speakerOnRef.current = true;
      setCanFlip(false);
      camerasRef.current = [];
      camIdxRef.current = 0;
      setPlaybackDevices([]);
      setActiveSpeaker("");
      activeSpeakerRef.current = "";
    };
  }, [creds]);

  const toggleMic = useCallback(async () => {
    const track = localAudioRef.current;
    if (!track) return;
    const next = !micOn;
    await track.setEnabled(next);
    setMicOn(next);
  }, [micOn]);

  const toggleCam = useCallback(async () => {
    const track = localVideoRef.current;
    if (!track) return;
    const next = !camOn;
    await track.setEnabled(next);
    setCamOn(next);
  }, [camOn]);

  // Browsers can't switch the physical output device generically, so "speaker"
  // toggles the incoming (remote) audio volume — i.e. mute/unmute the lawyer.
  const toggleSpeaker = useCallback(() => {
    const next = !speakerOn;
    speakerOnRef.current = next;
    clientRef.current?.remoteUsers?.forEach((u) => u.audioTrack?.setVolume(next ? 100 : 0));
    setSpeakerOn(next);
  }, [speakerOn]);

  // Pick which output device the remote (call) audio plays through.
  const setPlayback = useCallback(async (deviceId) => {
    activeSpeakerRef.current = deviceId;
    setActiveSpeaker(deviceId);
    const users = clientRef.current?.remoteUsers || [];
    for (const u of users) {
      try { await u.audioTrack?.setPlaybackDevice(deviceId); } catch (e) {}
    }
  }, []);

  // Cycle to the next camera (front <-> back on phones, or between webcams).
  const flipCamera = useCallback(async () => {
    const track = localVideoRef.current;
    const cams = camerasRef.current;
    if (!track || cams.length < 2) return;
    camIdxRef.current = (camIdxRef.current + 1) % cams.length;
    try {
      await track.setDevice(cams[camIdxRef.current].deviceId);
    } catch (e) {
      /* switch failed (device busy) — keep the current camera */
    }
  }, []);

  return {
    status, error, remoteUsers, localVideoTrack,
    micOn, camOn, speakerOn, canFlip, playbackDevices, activeSpeaker,
    toggleMic, toggleCam, toggleSpeaker, flipCamera, setPlayback,
  };
}
